import { useCallback, useEffect, useState } from 'react';
import { getErrorMessage } from '../api/client';
import {
  approveIngredientProposal, approveSupplierProposal, getCatalogPending, getIngredients, getInventoryUnits, getSuppliers,
  mergeIngredientProposal, mergeSupplierProposal, rejectIngredientProposal, rejectSupplierProposal,
} from '../api/inventory';
import { useAuth } from '../auth/AuthProvider';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Icon } from '../components/ui/Icon';
import { Button, LoadingPanel } from '../components/ui/primitives';
import { ActionBar } from '../components/ui/ActionBar';
import type { CatalogPendingItem, InventoryUnit } from '../types/inventory';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';
import { purchaseToReturnTo } from '../utils/purchaseBlockers';
import { describeProposal, editFormFor, ingredientApprovalEdits, supplierApprovalEdits, type ApprovalEditForm } from './catalogApprovalDetails';

const EMPTY_FORM: ApprovalEditForm = { name: '', baseUnitId: '', category: '', sku: '', contactName: '', phone: '', email: '' };

// Fase 26A (D5): bandeja mínima de aprobación de catálogo. OWNER/MANAGER ven proveedores/insumos
// propuestos por empleados y los resuelven: Aprobar / Usar existente (merge) / Rechazar. El backend es
// la autoridad; esta pantalla solo orquesta las acciones.
// 26A-3: cada propuesta muestra lo que el empleado definió (unidad base, categoría, SKU / contacto,
// teléfono, email) y "Editar y aprobar" permite corregirlo ahí mismo, no solo el nombre.
export function CatalogApprovalsPage() {
  const { user } = useAuth();
  const canApprove = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const [items, setItems] = useState<CatalogPendingItem[]>([]);
  const [activeSuppliers, setActiveSuppliers] = useState<Array<{ id: string; name: string }>>([]);
  const [activeIngredients, setActiveIngredients] = useState<Array<{ id: string; name: string }>>([]);
  const [units, setUnits] = useState<InventoryUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const [panel, setPanel] = useState<{ id: string; mode: 'reject' | 'merge' | 'edit' } | null>(null);
  const [reason, setReason] = useState('');
  const [mergeTarget, setMergeTarget] = useState('');
  const [editForm, setEditForm] = useState<ApprovalEditForm>(EMPTY_FORM);
  // P-078 (E · BK01): se llegó desde «Resolver ahora» de una compra bloqueada por catálogo pendiente
  // (`#catalog-approvals/volver/purchases/<id>`): se ofrece volver a esa compra (su enlace directo la reabre).
  const [returnToPurchase] = useState(() => purchaseToReturnTo(window.location.hash));
  const goBackToPurchase = () => { if (returnToPurchase) window.location.hash = `purchases/${returnToPurchase}`; };

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const [pending, sup, ing, unitList] = await Promise.all([getCatalogPending(), getSuppliers(), getIngredients(), getInventoryUnits()]);
      setItems(pending.items);
      setActiveSuppliers(sup.suppliers.filter((s) => (s.approvalStatus ?? 'ACTIVE') === 'ACTIVE').map((s) => ({ id: s.id, name: s.name })));
      setActiveIngredients(ing.ingredients.filter((i) => (i.approvalStatus ?? 'ACTIVE') === 'ACTIVE').map((i) => ({ id: i.id, name: i.name })));
      setUnits(unitList.units);
    } catch (e) { setError(getErrorMessage(e, 'No fue posible cargar las propuestas.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!canApprove) return <EmptyState title="Sin acceso" description="Solo un dueño o gerente puede aprobar catálogo." icon="warning" />;

  const closePanel = () => { setPanel(null); setReason(''); setMergeTarget(''); setEditForm(EMPTY_FORM); };
  const run = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    setBusyId(id); setError(undefined); setNotice(undefined);
    try { await fn(); setNotice(ok); closePanel(); await load(); }
    catch (e) { setError(getErrorMessage(e, 'No fue posible completar la acción.')); }
    finally { setBusyId(undefined); }
  };
  const approve = (it: CatalogPendingItem) => run(it.id, () => it.type === 'SUPPLIER' ? approveSupplierProposal(it.id) : approveIngredientProposal(it.id), 'Aprobado.');
  // Fase 26A: corregir lo que el empleado escribió mal y aprobar en un solo paso. 26A-3: no solo el
  // nombre — también unidad base/categoría/SKU (insumo) o contacto/teléfono/email (proveedor). Solo
  // viaja lo que cambió; sin cambios es una aprobación simple.
  const approveEdited = (it: CatalogPendingItem) => {
    if (it.type === 'SUPPLIER') {
      const edits = supplierApprovalEdits(it, editForm);
      return run(it.id, () => approveSupplierProposal(it.id, edits), edits ? 'Corregido y aprobado.' : 'Aprobado.');
    }
    const edits = ingredientApprovalEdits(it, editForm);
    return run(it.id, () => approveIngredientProposal(it.id, edits), edits ? 'Corregido y aprobado.' : 'Aprobado.');
  };
  const reject = (it: CatalogPendingItem) => run(it.id, () => it.type === 'SUPPLIER' ? rejectSupplierProposal(it.id, reason.trim()) : rejectIngredientProposal(it.id, reason.trim()), 'Rechazado.');
  // P-075: «Unir» es el nombre de la acción en toda la pantalla (antes «Ya existe: usar ese» y «Re-vinculado»).
  const merge = (it: CatalogPendingItem) => run(it.id, () => it.type === 'SUPPLIER' ? mergeSupplierProposal(it.id, mergeTarget) : mergeIngredientProposal(it.id, mergeTarget), 'Unido al existente.');

  // Unidades elegibles al corregir un insumo: las activas, más la propuesta aunque esté inactiva
  // (para que el select muestre el valor actual y no quede en blanco).
  const unitOptionsFor = (current: { id: string; name: string; symbol: string } | undefined) => {
    const options = units.filter((u) => u.isActive || u.id === current?.id).map((u) => ({ id: u.id, label: `${u.name} (${u.symbol})` }));
    if (!current || options.some((o) => o.id === current.id)) return options;
    return [{ id: current.id, label: `${current.name} (${current.symbol})` }, ...options];
  };
  const field = (key: keyof ApprovalEditForm) => (e: { target: { value: string } }) => setEditForm({ ...editForm, [key]: e.target.value });

  return (
    <div className="n3-screen">
      <PageHeader title="Aprobaciones de catálogo" description={`${items.length} pendiente${items.length === 1 ? '' : 's'} de aprobación`} />
      {/* «Volver» respeta una aprobación en curso, como los botones de la fila (Codex R2 · L5). */}
      {returnToPurchase && <div className="alert alert--info return-banner"><span>Vienes de una compra que espera este catálogo.</span><button className="text-button" disabled={Boolean(busyId)} onClick={goBackToPurchase} type="button">← Volver a la compra</button></div>}
      {notice && <div className="alert alert--success" role="status">{notice}</div>}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {loading ? (
        <LoadingPanel label="Cargando propuestas…" />
      ) : items.length === 0 ? (
        <EmptyState title="Todo al día" description="No hay proveedores ni insumos pendientes de aprobación." icon="success" />
      ) : (
        /* Pre-client UX hardening (móvil): tarjeta por propuesta (patrón de Compras/Cortes, sin
           colapso: aquí se DECIDE, así que las acciones quedan siempre visibles y ninguna se corta).
           Quién la propuso y cuándo viven en la tarjeta, no en columnas ocultas. */
        <div className="data-table-wrap">
          <table className="data-table data-table--cards data-table--cards-static">
            <thead><tr><th>Propuesta</th><th>Tienda</th><th>Compras abiertas</th><th>Acciones</th></tr></thead>
            <tbody>
              {items.map((it) => {
                const details = describeProposal(it);
                const busy = busyId === it.id;
                const kind = it.type === 'SUPPLIER' ? 'proveedor' : 'insumo';
                return (
                <tr key={`${it.type}-${it.id}`}>
                  <td className="dt-card-main">
                    <strong><StatusBadge variant={it.type === 'SUPPLIER' ? 'info' : 'neutral'}>{it.type === 'SUPPLIER' ? 'Proveedor' : 'Insumo'}</StatusBadge> {it.name}</strong>
                    {/* P-075 (S19): cada dato con su etiqueta encima, como en los detalles de la app. Antes iban
                        en una sola línea y se pegaban («Alberto RuizTeléfono: …»): la regla `td > small` de las
                        tablas le quitaba la separación a la fila de datos. */}
                    {details.length > 0 && <dl className="detail-list">{details.map((d) => <div key={d.label}><dt>{d.label}</dt><dd>{d.value}</dd></div>)}</dl>}
                    <small>Propuso {it.requestedByName ?? '—'} · {new Date(it.requestedAt).toLocaleDateString('es-MX', { dateStyle: 'medium', timeZone: DEFAULT_STORE_TIMEZONE })}</small>
                  </td>
                  <td data-label="Tienda">{it.requestedStoreName ?? '—'}</td>
                  <td data-label="Compras abiertas">{it.openPurchases > 0 ? <StatusBadge variant="warning">{it.openPurchases}</StatusBadge> : '0'}</td>
                  <td className="dt-card-actions">
                    {panel?.id === it.id && panel.mode === 'reject' ? (
                      <div className="approval-panel approval-panel--form">
                        {/* P-078 (C): campo con su etiqueta arriba y el control de siempre; las acciones en su barra. */}
                        <label className="field approval-field"><span>Motivo del rechazo</span><input placeholder="Escribe por qué se rechaza" value={reason} onChange={(e) => setReason(e.target.value)} /></label>
                        <ActionBar aria-label={`Rechazar ${it.name}`} danger={<button className="button button--danger" disabled={busy || !reason.trim()} type="button" onClick={() => void reject(it)}>Confirmar rechazo</button>}>
                          <button className="text-button" type="button" onClick={closePanel}>Cancelar</button>
                        </ActionBar>
                      </div>
                    ) : panel?.id === it.id && panel.mode === 'merge' ? (
                      <div className="approval-panel approval-panel--form">
                        {/* Unir no tiene vuelta atrás: la propuesta queda fusionada y ya no se puede reconsiderar. */}
                        <small>Sus compras pasarán al {kind} que elijas y la propuesta se cerrará. <strong>No se puede deshacer.</strong></small>
                        <label className="field approval-field"><span>{it.type === 'SUPPLIER' ? 'Proveedor existente' : 'Insumo existente'}</span>
                          <select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}>
                            <option value="">Elige el {kind} existente…</option>
                            {(it.type === 'SUPPLIER' ? activeSuppliers : activeIngredients).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </label>
                        <ActionBar aria-label={`Unir ${it.name}`} primary={<button className="button button--primary" disabled={busy || !mergeTarget} type="button" onClick={() => void merge(it)}>Unir al existente</button>}>
                          <button className="text-button" type="button" onClick={closePanel}>Cancelar</button>
                        </ActionBar>
                      </div>
                    ) : panel?.id === it.id && panel.mode === 'edit' ? (
                      /* P-078 (C · evidencia F): en el teléfono, etiqueta arriba, control de 48 px a todo el ancho y las
                         acciones debajo en su barra (primaria ancha, Cancelar aparte); en escritorio los campos siguen en
                         línea. Mismos campos, mismo payload (`ingredientApprovalEdits` / `supplierApprovalEdits`). */
                      <div className="approval-panel approval-panel--form">
                        <label className="field approval-field"><span>Nombre</span><input value={editForm.name} onChange={field('name')} /></label>
                        {it.type === 'INGREDIENT' ? (
                          <>
                            <label className="field approval-field"><span>Unidad base</span>
                              <select value={editForm.baseUnitId} onChange={field('baseUnitId')}>
                                {unitOptionsFor(it.baseUnit).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                              </select>
                            </label>
                            <label className="field approval-field"><span>Categoría</span><input placeholder="Opcional" value={editForm.category} onChange={field('category')} /></label>
                            <label className="field approval-field"><span>SKU</span><input placeholder="Opcional" value={editForm.sku} onChange={field('sku')} /></label>
                          </>
                        ) : (
                          <>
                            <label className="field approval-field"><span>Contacto</span><input placeholder="Opcional" value={editForm.contactName} onChange={field('contactName')} /></label>
                            <label className="field approval-field"><span>Teléfono</span><input placeholder="Opcional" value={editForm.phone} onChange={field('phone')} /></label>
                            <label className="field approval-field"><span>Email</span><input placeholder="Opcional" type="email" value={editForm.email} onChange={field('email')} /></label>
                          </>
                        )}
                        <ActionBar aria-label={`Editar y aprobar ${it.name}`} primary={<button className="button button--primary" disabled={busy || !editForm.name.trim() || (it.type === 'INGREDIENT' && !editForm.baseUnitId)} type="button" onClick={() => void approveEdited(it)}>Guardar y aprobar</button>}>
                          <button className="text-button" type="button" onClick={closePanel}>Cancelar</button>
                        </ActionBar>
                      </div>
                    ) : (
                      /* P-075 (S19): una sola acción dominante. «Aprobar» es la principal; «Editar y aprobar», la
                         alternativa; unir y rechazar van en texto, el rechazo en rojo.
                         P-078 (ActionBar v2): las mismas cuatro, por intención: Editar y aprobar · Unir como secundarias,
                         Aprobar a todo el ancho en el teléfono y Rechazar aparte, después de una línea. */
                      <ActionBar aria-label={`Acciones para ${it.name}`} className="approval-actions" danger={<Button disabled={busy} onClick={() => { setPanel({ id: it.id, mode: 'reject' }); setReason(''); }} variant="danger">Rechazar</Button>} primary={<Button disabled={busy} iconStart={<Icon name="check" size={16} />} onClick={() => void approve(it)} variant="primary">Aprobar</Button>}>
                        <Button disabled={busy} onClick={() => { setPanel({ id: it.id, mode: 'edit' }); setEditForm(editFormFor(it)); }} variant="secondary">Editar y aprobar</Button>
                        <Button disabled={busy} onClick={() => { setPanel({ id: it.id, mode: 'merge' }); setMergeTarget(''); }} title="Si ya está dado de alta con otro nombre, une esta propuesta a ese registro (sin duplicar)." variant="ghost">Unir con uno existente</Button>
                      </ActionBar>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="platform-muted">
        Consejo: revisa la unidad base de cada insumo antes de aprobarlo; después del primer movimiento de inventario ya no se puede cambiar.
        Si una propuesta tiene compras asociadas, no podrás rechazarla hasta resolver esas compras
        (aprobarla, unirla con uno existente, o resolver la compra desde Compras).
      </p>
    </div>
  );
}
